"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { api, useApi } from "@/lib/useApi";
import { ErrorBox, Notice } from "../ui";
import { Select } from "../Select";
import type { TableRow } from "./SettingsTable";
import { useDialog } from "@/components/Dialog";

type Mode = "prod" | "dev";
interface ModeView {
  available: boolean;
  mode: Mode;
  last: { state: "running" | "failed" | "done"; mode: Mode; at: number } | null;
  log: string | null;
}

/** How often the page asks while a switch runs (the app restarts at its end). */
const POLL_MS = 4_000;

/**
 * The installation's mode (Settings → Platform, administrators; only on a server set up by the installer): production
 * or development, as a row of the platform table (switched at once, no default) — and below the table what a switch
 * is doing (running, failed with its log). The switch runs on the server; the page asks until the app answers in the
 * new mode, then reloads. Null row: not available here.
 */
export function useModeRow(): { row: TableRow | null; status: ReactNode } {
  const { t, f } = useI18n();
  const dialog = useDialog();
  const { data, reload } = useApi<ModeView>("/api/platform/mode");
  const [target, setTarget] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const failed = !!target && data?.last?.state === "failed" && data.last.mode === target;
  const switching = !!target && !failed;

  // while switching: ask again and again (the app is away for a moment when it restarts)
  useEffect(() => {
    if (!switching) return;
    const id = setInterval(reload, POLL_MS);
    return () => clearInterval(id);
  }, [switching, reload]);
  // the app answers in the new mode: the page in the new mode
  useEffect(() => {
    if (target && data?.mode === target) window.location.reload();
  }, [target, data?.mode]);

  if (!data?.available) return { row: null, status: null };
  const label = (m: Mode) => t(`set.mode.${m}`);
  const choose = async (m: Mode) => {
    if (m === data.mode || switching || !(await dialog.confirm(t("set.mode.confirm", { mode: label(m) })))) return;
    setError(null);
    try {
      await api("/api/platform/mode", { method: "POST", json: { mode: m } });
      setTarget(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  // the last switch done belongs to the row's description (it is no state of the page)
  const done = data.last?.state === "done" ? ` ${t("set.mode.done", { mode: label(data.last.mode), date: f.date(data.last.at) })}` : "";
  const row: TableRow = {
    key: "mode",
    label: t("set.mode.title"),
    description: `${t("set.mode.hint")}${done}`,
    input: <Select<Mode> label={t("set.mode.title")} value={data.mode} options={(["dev", "prod"] as const).map((m) => ({ value: m, label: label(m) }))} onChange={(m) => void choose(m)} disabled={switching} className="w-full" />,
  };
  const status = (
    <>
      {switching && <Notice>{t("set.mode.running", { mode: label(target) })}</Notice>}
      {failed && <ErrorBox>{t("set.mode.failed", { mode: label(target) })}</ErrorBox>}
      {failed && data.log && <pre className="max-h-48 overflow-auto rounded-md bg-surface-2 p-2 text-[11px] text-muted">{data.log}</pre>}
      {error && <ErrorBox>{error}</ErrorBox>}
    </>
  );
  return { row, status };
}
